import { onMounted, onUnmounted } from 'vue'
import { Capacitor } from '@capacitor/core'

// Define the shape of the actions used by the StatusPage script
interface StatusPageActions {
    showFrame: (data?: any) => void;
    dismissFrame: (data?: any) => void;
    [key: string]: (data?: any) => void; // Allow indexing by string
}

export function useStatusPage() {
    let messageHandler: ((event: MessageEvent) => void) | null = null
    let iframeElement: HTMLIFrameElement | null = null

    // Helper to apply styles with type safety
    const applyStyles = (element: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
        Object.assign(element.style, styles)
    }

    onMounted(() => {
        // 1. Guard: Only run on Web
        if (Capacitor.getPlatform() !== 'web') return

        console.debug('[STATUSPAGE] Initializing widget...')

        // 2. Setup the Iframe
        const frame = document.createElement('iframe')
        frame.src = 'https://wynq251rmtz6.statuspage.io/embed/frame'
        frame.title = 'Platcop Status'
        frame.setAttribute('aria-hidden', 'true')

        // Base Styles
        applyStyles(frame, {
            position: 'fixed',
            border: 'none',
            boxShadow: '0 20px 32px -8px rgba(9,20,66,0.25)',
            zIndex: '9999',
            transition: 'left 1s ease, bottom 1s ease, right 1s ease'
        })

        // 3. Mobile vs Desktop Logic
        // using window.innerWidth is generally more reliable than screen.width for responsive web
        const isMobile = window.innerWidth < 450

        if (isMobile) {
            frame.src += '?mobile=true'
            applyStyles(frame, {
                height: '20vh', width: '100vw', left: '-9999px', bottom: '-9999px'
            })
        } else {
            applyStyles(frame, {
                height: '115px', width: '320px', left: 'auto', right: '-9999px', bottom: '60px'
            })
        }

        // 4. Inject
        document.body.appendChild(frame)
        iframeElement = frame

        // 5. Define Actions
        const actions: StatusPageActions = {
            showFrame: () => {
                frame.tabIndex = 0
                if (isMobile) {
                    applyStyles(frame, { left: '0', bottom: '0' })
                } else {
                    applyStyles(frame, { left: 'auto', right: '60px' })
                }
            },
            dismissFrame: () => {
                applyStyles(frame, { left: '-9999px' })
                frame.tabIndex = -1
            }
        }

        // 6. Listen for messages from StatusPage
        messageHandler = (event: MessageEvent) => {
            // Validate that event.data exists and has an action property
            if (event.data && typeof event.data === 'object' && 'action' in event.data) {
                const actionName = event.data.action as string

                if (actions[actionName]) {
                    actions[actionName](event.data)
                }
            }
        }

        window.addEventListener('message', messageHandler, false)

        // Optional: Type-safe way to attach test function to window
        // (requires extending Window interface globally, or just casting as any for quick debug)
        ;(window as any).statusEmbedTest = actions.showFrame
    })

    // 7. Cleanup
    onUnmounted(() => {
        if (messageHandler) {
            window.removeEventListener('message', messageHandler)
        }
        if (iframeElement && document.body.contains(iframeElement)) {
            document.body.removeChild(iframeElement)
        }
    })
}
